import './left-nav.scss';

import React, { memo, useMemo, useCallback, useRef, useEffect, useContext, MouseEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Typography } from '../../components/typography/typography';
import { ActionWidget } from '../action-widget/action-widget';
import { Button } from '../../components/form/button';
import { useResponsive } from '../../hooks/use-reponsive';
import { TenantContext } from '../../context/tenant-context';
import classNames from 'classnames';
import { useNavigation, NavItemData } from './hooks/use-navigation';
import { openEcommerceSite } from '../header/utils/admin-header-utils';
import useRememberQueryParams from '../../hooks/use-remember-query-params';
import Icon from '../../components/icons/icon';

// Types
interface NavItemProps extends NavItemData {
  isOpen?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
  className?: string;
}

// Memoized NavItem component
const NavItem = memo<NavItemProps>(
  ({
    label,
    link,
    absoluteLink,
    icon,
    legacyLink = false,
    children,
    isOpen = false,
    isCollapsed = false,
    onToggle,
    onClick,
    onItemClick,
    className = '',
  }) => {
    const { contextPath } = useContext(TenantContext);
    const location = useLocation();
    const queryParams = useRememberQueryParams(link || '');

    // Check if this item has children (submenu)
    const hasChildren = children && children.length > 0;

    const handleClick = useCallback(
      (e: MouseEvent) => {
        // If item has children, only toggle submenu (don't navigate)
        if (hasChildren) {
          e.preventDefault();
          onToggle?.();
          // Don't call onClick for submenu toggles
          return;
        }

        // Handle custom onClick function from the nav item data
        if (onItemClick) {
          e.preventDefault();
          onItemClick(); // Execute the custom function
        }

        // For items without children, handle navigation
        if (!absoluteLink && !link) {
          e.preventDefault();
        }

        // Call onClick for actual navigation (will close mobile overlay)
        onClick?.();
      },
      [absoluteLink, link, hasChildren, onToggle, onClick, onItemClick],
    );

    // Handler for child items - always navigate and close overlay
    const handleChildClick = useCallback(() => {
      onClick?.();
    }, [onClick]);

    const renderLink = useMemo(() => {
      const content = (
        <>
          {icon}
          <Typography type="text-sm" aria-hidden={isCollapsed}>
            {label}
          </Typography>
          {/* Add chevron indicator for items with children */}
          {hasChildren && (
            <div className="grow flex justify-end">
              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
            </div>
          )}
        </>
      );

      const linkClasses = classNames('navitem', className, {
        'has-submenu': hasChildren,
      });

      if (absoluteLink && !hasChildren) {
        return (
          <a
            href={absoluteLink}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClasses}
            onClick={handleClick}
          >
            {content}
          </a>
        );
      }

      if (link && !hasChildren) {
        if (legacyLink) {
          return (
            <a
              href={`${contextPath}manager/${link}`}
              className={classNames(linkClasses, {
                active: `/manager/${link}` === location.pathname,
              })}
              onClick={handleClick}
            >
              {content}
            </a>
          );
        }

        return (
          <NavLink
            end
            className={({ isActive }) => classNames(linkClasses, { active: isActive })}
            to={{
              pathname: `${contextPath}manager/${link}`,
              search: queryParams?.toString(),
            }}
            onClick={handleClick}
          >
            {content}
          </NavLink>
        );
      }

      // For items with children or items without links, render as button/div
      return (
        <div
          className={classNames(linkClasses, 'submenu-toggle')}
          onClick={handleClick}
          role={hasChildren ? 'button' : undefined}
          aria-expanded={hasChildren ? isOpen : undefined}
          tabIndex={hasChildren ? 0 : undefined}
        >
          {content}
        </div>
      );
    }, [
      absoluteLink,
      link,
      legacyLink,
      hasChildren,
      contextPath,
      location.pathname,
      handleClick,
      icon,
      label,
      className,
      queryParams,
      isOpen,
    ]);

    return (
      <li>
        {renderLink}
        {hasChildren && (
          <ul className={classNames('nav-sub-ul', { 'show-submenu': isOpen })} aria-expanded={isOpen}>
            {children.map((child) => (
              <NavItem
                key={child.label}
                {...child}
                // Pass handleChildClick to ensure child items close the overlay
                onClick={handleChildClick}
              />
            ))}
          </ul>
        )}
      </li>
    );
  },
);

NavItem.displayName = 'NavItem';

// Main navigation menu component
const NavigationMenu = memo<{
  items: NavItemData[];
  isCollapsed: boolean;
  openSubmenu: string | null;
  onToggleSubmenu: (label: string) => void;
  onItemClick?: () => void;
}>(({ items, isCollapsed, openSubmenu, onToggleSubmenu, onItemClick }) => {
  const handleToggle = useCallback(
    (label: string) => {
      onToggleSubmenu(label);
    },
    [onToggleSubmenu],
  );

  return (
    <ul className="nav-list">
      {items.map((item) => (
        <NavItem
          key={item.label}
          {...item}
          isOpen={openSubmenu === item.label}
          isCollapsed={isCollapsed}
          onToggle={item.children ? () => handleToggle(item.label) : undefined}
          onClick={onItemClick}
        />
      ))}
    </ul>
  );
});

NavigationMenu.displayName = 'NavigationMenu';

// Desktop navigation component - Fixed to show as sidebar
const DesktopNavigation = memo<{
  isHover: boolean;
  isCollapsed: boolean;
  onNavChange: (hover: boolean) => void;
  children: React.ReactNode;
}>(({ isHover, isCollapsed, onNavChange, children }) => {
  const handleMouseEnter = useCallback(() => {
    if (isCollapsed) onNavChange(true);
  }, [isCollapsed, onNavChange]);

  const handleMouseLeave = useCallback(() => {
    if (isCollapsed) onNavChange(false);
  }, [isCollapsed, onNavChange]);

  return (
    <nav
      className={classNames('left-nav', {
        collapsed: isCollapsed,
        hover: isCollapsed && isHover,
        'not-hover': isCollapsed && !isHover,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </nav>
  );
});

DesktopNavigation.displayName = 'DesktopNavigation';

// Mobile navigation component - Shows hamburger menu and overlay
const MobileNavigation = memo<{
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  children: React.ReactNode;
}>(({ isOpen, onToggle, children }) => {
  const handleOpen = useCallback(() => onToggle(true), [onToggle]);
  const handleClose = useCallback(() => onToggle(false), [onToggle]);

  return (
    <nav className="left-nav-mobile">
      {/* Top mobile navbar */}
      <div className="navbar">
        <div>
          <Button onClick={handleOpen} aria-label="Open navigation menu" className="button-tertiary !pl-0">
            <Icon icon="HamburguerMenu" stroke="var(--grey-400)" />
          </Button>
          <Icon icon="QuipliLogo" size="xlarge" fill="var(--quipli-white-50)" />
        </div>
        <div>
          <Button className="button-tertiary" onClick={openEcommerceSite} aria-label="Open main site">
            <Icon icon="World" />
          </Button>
          <ActionWidget className="header" />
        </div>
      </div>

      {/* Mobile overlay menu */}
      {isOpen && (
        <>
          <div className="navbar-background" onClick={handleClose} aria-label="Close navigation menu" />
          <div className="navbar-content">{children}</div>
        </>
      )}
    </nav>
  );
});

MobileNavigation.displayName = 'MobileNavigation';

// Navigation header component - Different for mobile vs desktop
const NavigationHeader = memo<{
  isMobile: boolean;
  isCollapsed: boolean;
  isHover?: boolean;
  onToggleCollapse: () => void;
  onClose?: () => void;
}>(({ isMobile, isCollapsed, isHover = false, onToggleCollapse, onClose }) => (
  <div className="nav-header">
    {isMobile ? (
      // Mobile header - just close button
      <Button onClick={onClose} aria-label="Close navigation" className="button-tertiary !pl-0">
        <Icon icon="Close" size="medium" stroke="var(--quipli-white-50)" />
      </Button>
    ) : (
      // Desktop header - collapse button and logo
      <>
        <Button
          className={classNames('collapse-icon button-tertiary', { active: !isCollapsed })}
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Icon icon="SideBar" stroke="var(--grey-400)" />
        </Button>
        <div className={classNames({ hidden: isCollapsed && !isHover })}>
          <Icon icon="QuipliLogo" size="xlarge" fill="var(--quipli-white-50)" />
        </div>
      </>
    )}
  </div>
));

NavigationHeader.displayName = 'NavigationHeader';

// Main LeftNav component
export const LeftNav = memo<{
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}>(({ isCollapsed, setIsCollapsed }) => {
  const { isMobileOrTablet } = useResponsive();
  const [isHover, setIsHover] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [openSubmenu, setOpenSubmenu] = React.useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  const { mainNavItems, footerNavItems, adminProfileNavItems } = useNavigation();

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  const handleToggleSubmenu = useCallback((label: string) => {
    setOpenSubmenu((prev) => (prev === label ? null : label));
  }, []);

  const handleNavChange = useCallback((hover: boolean) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHover(hover);
    }, 200);
  }, []);

  const handleMobileItemClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMobileClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Fallback content if no navigation data
  const fallbackNavigationContent = useMemo(
    () => (
      <>
        <div className="nav-header">
          {isMobileOrTablet ? (
            <Button onClick={handleMobileClose} aria-label="Close navigation" className="button-tertiary !pl-0">
              <Icon icon="Close" size="medium" stroke="var(--quipli-white-50)" />
            </Button>
          ) : (
            <>
              <Button
                className={classNames('collapse-icon button-tertiary', { active: !isCollapsed })}
                onClick={handleToggleCollapse}
                aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
              >
                <Icon icon="SideBar" stroke="var(--grey-400)" />
              </Button>
              <div className={classNames({ hidden: isCollapsed })}>
                <Icon icon="QuipliLogo" size="xlarge" fill="var(--quipli-white-50)" />
              </div>
            </>
          )}
        </div>
        <ul className="nav-list">
          <li>
            <NavLink to="/manager/home" className="navitem">
              <Icon icon="Dashboard" stroke="var(--quipli-white-50)" />
              <Typography type="text-sm">Dashboard</Typography>
            </NavLink>
          </li>
          <li>
            <NavLink to="/manager/orders" className="navitem">
              <Icon icon="ShoppingBag" stroke="var(--quipli-white-50)" />
              <Typography type="text-sm">Orders</Typography>
            </NavLink>
          </li>
        </ul>
        <div className="nav-footer">
          <ul className="nav-list">
            <li>
              <a
                href="https://quiplihelp.zendesk.com/hc/en-us/"
                target="_blank"
                rel="noopener noreferrer"
                className="navitem"
              >
                <Icon icon="HelpCircle" stroke="var(--quipli-white-50)" />
                <Typography type="text-sm">Help Center</Typography>
              </a>
            </li>
          </ul>
        </div>
      </>
    ),
    [isMobileOrTablet, isCollapsed, handleToggleCollapse, handleMobileClose],
  );

  const navigationContent = useMemo(() => {
    // If no navigation data, show fallback
    if (!mainNavItems.length && !footerNavItems.length) {
      return fallbackNavigationContent;
    }

    return (
      <>
        <NavigationHeader
          isMobile={isMobileOrTablet}
          isCollapsed={isCollapsed}
          isHover={isHover}
          onToggleCollapse={handleToggleCollapse}
          onClose={isMobileOrTablet ? handleMobileClose : undefined}
        />
        <NavigationMenu
          items={mainNavItems}
          isCollapsed={isCollapsed}
          openSubmenu={openSubmenu}
          onToggleSubmenu={handleToggleSubmenu}
          onItemClick={isMobileOrTablet ? handleMobileItemClick : undefined}
        />
        <div className="nav-footer">
          <NavigationMenu
            items={footerNavItems}
            isCollapsed={isCollapsed}
            openSubmenu={openSubmenu}
            onToggleSubmenu={handleToggleSubmenu}
            onItemClick={isMobileOrTablet ? handleMobileItemClick : undefined}
          />
        </div>
        <div className="nav-footer">
          <NavigationMenu
            items={adminProfileNavItems}
            isCollapsed={isCollapsed}
            openSubmenu={openSubmenu}
            onToggleSubmenu={handleToggleSubmenu}
            onItemClick={isMobileOrTablet ? handleMobileItemClick : undefined}
          />
        </div>
      </>
    );
  }, [
    mainNavItems,
    footerNavItems,
    adminProfileNavItems,
    fallbackNavigationContent,
    isMobileOrTablet,
    isCollapsed,
    isHover,
    handleToggleCollapse,
    handleMobileClose,
    openSubmenu,
    handleToggleSubmenu,
    handleMobileItemClick,
  ]);

  if (isMobileOrTablet) {
    return (
      <MobileNavigation isOpen={isOpen} onToggle={setIsOpen}>
        {navigationContent}
      </MobileNavigation>
    );
  }

  return (
    <DesktopNavigation isHover={isHover} isCollapsed={isCollapsed} onNavChange={handleNavChange}>
      {navigationContent}
    </DesktopNavigation>
  );
});

LeftNav.displayName = 'LeftNav';
